import React from 'react';
import { render, act } from '@testing-library/react';
import Announce from '../Announce';
import API from '../../api';

// Mock the API module
jest.mock('../../api', () => ({
  getAnnouncements: jest.fn(),
  sendAnnouncementEvent: jest.fn(),
}));

// Mock ReactDOM.createPortal to render children inline
jest.mock('react-dom', () => ({
  ...jest.requireActual('react-dom'),
  createPortal: (node) => node,
}));

// Mock child components
jest.mock('../TopBar', () => {
  return function MockTopBar(props) {
    return <div data-testid="top-bar" data-announcements={JSON.stringify(props.announcements)} />;
  };
});

jest.mock('../ChatBubble', () => {
  return function MockChatBubble(props) {
    return <div data-testid="chat-bubble" data-announcements={JSON.stringify(props.announcements)} />;
  };
});

const defaultProps = {
  protocol: 'https',
  host: 'example.com',
  site: 'test-site',
};

const sampleAnnouncements = [
  { uuid: 'abc-123', announcement_type: 'bar', body: 'Top bar message', reaction_counts: { '👍': 5 } },
  { uuid: 'def-456', announcement_type: 'bubble', body: 'Chat bubble message', reaction_counts: { '❤️': 3 } },
];

describe('Announce', () => {
  let portalContainers;

  beforeEach(() => {
    jest.clearAllMocks();
    window.__a_announcements_state = undefined;
    window.localStorage.clear();

    // Create portal target elements
    portalContainers = ['a-top-bar-container', 'a-chat-bubble-container'].map(id => {
      const el = document.createElement('div');
      el.id = id;
      document.body.appendChild(el);
      return el;
    });
  });

  afterEach(() => {
    portalContainers.forEach(el => document.body.removeChild(el));
  });

  it('renders the announcements wrapper', async () => {
    API.getAnnouncements.mockResolvedValue([]);
    const { container } = render(<Announce {...defaultProps} />);
    expect(container.querySelector('.a-announcements')).toBeTruthy();
  });

  it('fetches announcements on mount', async () => {
    API.getAnnouncements.mockResolvedValue(sampleAnnouncements);

    await act(async () => {
      render(<Announce {...defaultProps} />);
    });

    expect(API.getAnnouncements).toHaveBeenCalledWith('https', 'example.com', 'test-site');
  });

  it('separates announcements by type into TopBar and ChatBubble', async () => {
    API.getAnnouncements.mockResolvedValue(sampleAnnouncements);

    let result;
    await act(async () => {
      result = render(<Announce {...defaultProps} />);
    });

    const topBar = result.getByTestId('top-bar');
    const chatBubble = result.getByTestId('chat-bubble');

    const topBarAnnouncements = JSON.parse(topBar.getAttribute('data-announcements'));
    const chatBubbleAnnouncements = JSON.parse(chatBubble.getAttribute('data-announcements'));

    expect(topBarAnnouncements).toHaveLength(1);
    expect(topBarAnnouncements[0].uuid).toBe('abc-123');
    expect(chatBubbleAnnouncements).toHaveLength(1);
    expect(chatBubbleAnnouncements[0].uuid).toBe('def-456');
  });

  it('does not fetch if lastFetch is recent (cached state)', async () => {
    window.__a_announcements_state = {
      topBars: [sampleAnnouncements[0]],
      chatBubbles: [sampleAnnouncements[1]],
      lastFetch: new Date(),
    };

    API.getAnnouncements.mockResolvedValue([]);

    await act(async () => {
      render(<Announce {...defaultProps} />);
    });

    expect(API.getAnnouncements).not.toHaveBeenCalled();
  });

  it('fetches if lastFetch is older than 5 minutes', async () => {
    window.__a_announcements_state = {
      topBars: [],
      chatBubbles: [],
      lastFetch: new Date(Date.now() - 400000), // ~6.7 minutes ago
    };

    API.getAnnouncements.mockResolvedValue([]);

    await act(async () => {
      render(<Announce {...defaultProps} />);
    });

    expect(API.getAnnouncements).toHaveBeenCalled();
  });

  it('saves state to window on unmount', async () => {
    API.getAnnouncements.mockResolvedValue(sampleAnnouncements);

    let result;
    await act(async () => {
      result = render(<Announce {...defaultProps} />);
    });

    result.unmount();
    expect(window.__a_announcements_state).toBeDefined();
    expect(window.__a_announcements_state.topBars).toBeDefined();
  });

  it('handles API error gracefully', async () => {
    API.getAnnouncements.mockRejectedValue(new Error('Network error'));
    const consoleSpy = jest.spyOn(console, 'log').mockImplementation();

    await act(async () => {
      render(<Announce {...defaultProps} />);
    });

    // Should not throw
    expect(consoleSpy).toHaveBeenCalledWith('API Error', expect.any(Error));
    consoleSpy.mockRestore();
  });
});
